import { useEffect, useRef, useState } from 'react';

/**
 * Select — custom styled dropdown that replaces the native <select> in places
 * where we want a modern look matching the rest of the design system.
 *
 * Props:
 *  - options: string[] (or { value, label }[])
 *  - value: string (current selected value)
 *  - onChange: (value) => void
 *  - placeholder: string (optional)
 */
export default function Select({ options = [], value, onChange, placeholder = 'Select…' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const normalized = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const selected = normalized.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-2 font-semibold bg-white"
        style={{
          padding: '8px 14px',
          borderRadius: 10,
          border: '1px solid var(--line)',
          fontSize: 13,
          color: 'var(--ink)',
          fontFamily: 'inherit',
          boxShadow: open ? '0 0 0 3px rgba(34,181,114,0.18)' : 'none',
          transition: 'box-shadow .15s',
        }}
      >
        <span>{selected ? selected.label : placeholder}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .15s', opacity: 0.65 }}>
          <polyline points="6,9 12,15 18,9" />
        </svg>
      </button>

      {open && (
        <div role="listbox"
          className="absolute right-0 bg-white rounded-xl overflow-hidden"
          style={{
            top: 'calc(100% + 6px)',
            minWidth: '100%',
            boxShadow: 'var(--shadow-lg)',
            border: '1px solid var(--line)',
            zIndex: 30,
            padding: 6,
          }}
        >
          {normalized.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => { onChange?.(o.value); setOpen(false); }}
                className="w-full text-left flex items-center gap-2 rounded-lg whitespace-nowrap"
                style={{
                  padding: '8px 12px',
                  fontSize: 13,
                  background: active ? 'rgba(34,181,114,0.1)' : 'transparent',
                  color: active ? 'var(--green-2)' : 'var(--ink)',
                  fontWeight: active ? 600 : 500,
                }}
              >
                <span className="flex-1">{o.label}</span>
                {active && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
